export const ADVANCED_STATS_STORAGE_KEY = 'playerAdvancedStats';
export const RELAY_STATUS_STALE_MS = 3000;
export const ULTRA_HISTORY_POINTS = 61;

const PLAYER_C_TOKEN_PATTERN = '[A-Za-z0-9_-]{32}';
const PLAYLIST_PATH = new RegExp(`/player-c/(${PLAYER_C_TOKEN_PATTERN})/index\\.m3u8$`);
const STATUS_PATH = new RegExp(`/player-c/(${PLAYER_C_TOKEN_PATTERN})/status\\.json$`);
const STATUS_BACKOFF_MS = [2000, 4000, 8000, 15000];
const RELAY_STATES = new Set(['waiting', 'starting', 'ready', 'recovering', 'stopped']);

export class UltraSessionHttpError extends Error {
  constructor(statusCode) {
    super(`Player C session request failed with HTTP ${statusCode}`);
    this.name = 'UltraSessionHttpError';
    this.statusCode = Number(statusCode) || null;
  }
}

function finiteOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function readAdvancedStatsPreference(storage) {
  try {
    return storage?.getItem(ADVANCED_STATS_STORAGE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

export function browserLocalStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch (_) {
    return null;
  }
}

export function writeAdvancedStatsPreference(storage, enabled) {
  try {
    storage?.setItem(ADVANCED_STATS_STORAGE_KEY, enabled ? 'true' : 'false');
    return true;
  } catch (_) {
    return false;
  }
}

export function buildUltraSessionRequestUrl(playURL, viewId, baseUrl) {
  const fallbackBase =
    baseUrl || (typeof window !== 'undefined' ? window.location.href : 'http://localhost/');
  const url = new URL(playURL, fallbackBase);
  url.searchParams.set('_player_c_session', '1');
  if (viewId) url.searchParams.set('_vid', viewId);
  return url.href;
}

/**
 * Accept only the two opaque, same-origin Player C resources returned by the
 * control plane. This deliberately prevents a malformed response from making
 * the authenticated browser poll or play an arbitrary URL.
 */
export function validateUltraSessionPayload(payload, baseUrl) {
  if (!payload || typeof payload !== 'object') return null;
  try {
    const runtimeBase = new URL(
      baseUrl || (typeof window !== 'undefined' ? window.location.href : 'http://localhost/')
    );
    const playlistUrl = new URL(payload.playlistUrl, runtimeBase);
    const statusUrl = new URL(payload.statusUrl, runtimeBase);
    if (
      playlistUrl.origin !== runtimeBase.origin ||
      statusUrl.origin !== runtimeBase.origin ||
      playlistUrl.search ||
      playlistUrl.hash ||
      statusUrl.search ||
      statusUrl.hash
    ) {
      return null;
    }
    const playlistMatch = playlistUrl.pathname.match(PLAYLIST_PATH);
    const statusMatch = statusUrl.pathname.match(STATUS_PATH);
    if (!playlistMatch || !statusMatch || playlistMatch[1] !== statusMatch[1]) return null;
    return {
      playlistUrl: playlistUrl.href,
      statusUrl: statusUrl.href,
      state: RELAY_STATES.has(payload.state) ? payload.state : 'starting'
    };
  } catch (_) {
    return null;
  }
}

export async function resolveUltraSession(playURL, viewId, {signal, fetchImpl, baseUrl} = {}) {
  const request = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!request) return {playlistUrl: playURL, statusUrl: null, state: null, legacy: true};
  try {
    const requestUrl = buildUltraSessionRequestUrl(playURL, viewId, baseUrl);
    const response = await request(requestUrl, {
      method: 'GET',
      headers: {Accept: 'application/json'},
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'follow',
      signal
    });
    const contentType = String(response?.headers?.get?.('content-type') || '').toLowerCase();
    if (!response.ok) {
      try {
        await response.body?.cancel?.();
      } catch (_) {
        // Releasing the failed response is best-effort; classification still follows.
      }
      throw new UltraSessionHttpError(response.status);
    }
    if (!contentType.includes('json')) {
      try {
        // An older backend may have opened an unbounded MPEG-TS body. Close it
        // before the real media transport reopens /play, otherwise one view can
        // temporarily own two HTTP streams and two upstream slots.
        await response.body?.cancel?.();
      } catch (_) {
        // Legacy playback can still continue through a clean media request.
      }
      return {playlistUrl: playURL, statusUrl: null, state: null, legacy: true};
    }
    const validated = validateUltraSessionPayload(await response.json(), baseUrl);
    return validated
      ? {...validated, legacy: false}
      : {playlistUrl: playURL, statusUrl: null, state: null, legacy: true};
  } catch (error) {
    if (error?.name === 'AbortError' || error instanceof UltraSessionHttpError) throw error;
    return {playlistUrl: playURL, statusUrl: null, state: null, legacy: true};
  }
}

export function validateRelayStatusPayload(payload, receivedAtMs = Date.now()) {
  if (!payload || typeof payload !== 'object' || !RELAY_STATES.has(payload.state)) return null;
  if (typeof payload.upstreamReceiving !== 'boolean') return null;
  const reserveSeconds =
    payload.reserveSeconds === null ? null : finiteOrNull(payload.reserveSeconds);
  const targetReserveSeconds = finiteOrNull(payload.targetReserveSeconds);
  const sampledAtMs = finiteOrNull(payload.sampledAtMs);
  if (
    (payload.reserveSeconds !== null && reserveSeconds === null) ||
    targetReserveSeconds === null ||
    targetReserveSeconds <= 0 ||
    sampledAtMs === null ||
    sampledAtMs <= 0
  ) {
    return null;
  }
  return {
    state: payload.state,
    upstreamReceiving: payload.upstreamReceiving,
    reserveSeconds: reserveSeconds === null ? null : Math.max(0, reserveSeconds),
    targetReserveSeconds,
    sampledAtMs,
    receivedAtMs
  };
}

export function relayStatusBackoffMs(failureCount) {
  const index = Math.max(0, Math.floor(failureCount || 1) - 1);
  return STATUS_BACKOFF_MS[Math.min(index, STATUS_BACKOFF_MS.length - 1)];
}

export function relaySampleIsFresh(sample, now = Date.now()) {
  return Boolean(
    sample &&
      Number.isFinite(sample.receivedAtMs) &&
      now - sample.receivedAtMs <= RELAY_STATUS_STALE_MS
  );
}

/**
 * Strict diagnostic measurement: only the range containing currentTime is
 * counted. Unlike bufferedSecondsAhead, a wholly-future startup range is not
 * treated as available at the playhead.
 */
export function strictBufferedSecondsAhead(buffered, currentTime = 0) {
  if (!buffered || typeof buffered.length !== 'number') return null;
  try {
    for (let index = 0; index < buffered.length; index += 1) {
      const start = Number(buffered.start(index));
      const end = Number(buffered.end(index));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      if (currentTime >= start && currentTime <= end) return Math.max(0, end - currentTime);
    }
    return 0;
  } catch (_) {
    return null;
  }
}

function historyPoint(at, values = {}) {
  return {
    at,
    reserveSeconds: finiteOrNull(values.reserveSeconds),
    localBufferSeconds: finiteOrNull(values.localBufferSeconds),
    targetReserveSeconds: finiteOrNull(values.targetReserveSeconds)
  };
}

/** Add/replace a one-second bucket and explicitly fill elapsed buckets as gaps. */
export function appendUltraHistory(history, values, now = Date.now()) {
  const bucket = Math.floor(now / 1000) * 1000;
  const next = Array.isArray(history) ? [...history] : [];
  const last = next[next.length - 1];
  if (last?.at === bucket) {
    next[next.length - 1] = historyPoint(bucket, values);
    return next.slice(-ULTRA_HISTORY_POINTS);
  }
  if (last?.at > bucket) return [historyPoint(bucket, values)];
  if (last?.at < bucket) {
    const firstRetainedBucket = bucket - (ULTRA_HISTORY_POINTS - 1) * 1000;
    for (let at = Math.max(last.at + 1000, firstRetainedBucket); at < bucket; at += 1000) {
      next.push(historyPoint(at));
    }
  }
  next.push(historyPoint(bucket, values));
  return next.slice(-ULTRA_HISTORY_POINTS);
}

export function reserveTrendOverFiveSeconds(history, now = Date.now()) {
  const cutoff = now - 5000;
  const samples = (Array.isArray(history) ? history : []).filter(
    (point) => point.at >= cutoff && Number.isFinite(point.reserveSeconds)
  );
  if (samples.length < 2) return null;
  return samples[samples.length - 1].reserveSeconds - samples[0].reserveSeconds;
}

export function reservePresentation(sample, history, now = Date.now()) {
  const fresh = relaySampleIsFresh(sample, now);
  const reserve = fresh ? finiteOrNull(sample.reserveSeconds) : null;
  if (!fresh) {
    return {label: 'Datos no disponibles', color: 'default', reserveSeconds: null};
  }
  if (reserve === null) {
    return {label: 'Reconstruyendo', color: 'info', reserveSeconds: null};
  }
  if (reserve <= 0.05) {
    return sample.upstreamReceiving
      ? {label: 'Al día, sin reserva', color: 'warning', reserveSeconds: 0}
      : {label: 'Colchón agotado', color: 'error', reserveSeconds: 0};
  }
  const trend = reserveTrendOverFiveSeconds(history, now);
  if (!sample.upstreamReceiving || (trend !== null && trend <= -0.5)) {
    return {label: 'Usando colchón', color: 'warning', reserveSeconds: reserve};
  }
  const target = finiteOrNull(sample.targetReserveSeconds);
  if (
    sample.state === 'recovering' ||
    (trend !== null && trend >= 0.5) ||
    (target !== null && target > 0 && reserve < target * 0.75)
  ) {
    return {label: 'Reconstruyendo', color: 'info', reserveSeconds: reserve};
  }
  return {label: 'Protegido', color: 'success', reserveSeconds: reserve};
}

export function formatSeconds(value) {
  return Number.isFinite(value) ? `${value.toFixed(1).replace('.', ',')} s` : '—';
}

export function formatBitrate(value) {
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace('.', ',')} Mb/s`;
  return `${Math.round(value / 1000)} kb/s`;
}

export function playerStateLabel(state) {
  const labels = {
    preparing: 'Preparando',
    connecting: 'Conectando',
    stabilizing: 'Estabilizando',
    playing: 'Reproduciendo',
    paused: 'En pausa',
    buffering: 'Almacenando',
    frozen: 'Congelado (corte de origen)',
    waitingSlot: 'Esperando pantalla libre',
    offline: 'Sin conexión',
    waitingRetry: 'Esperando reintento',
    recovered: 'Recuperado',
    terminalError: 'Error permanente'
  };
  return labels[state] || state || '—';
}

export function relayStateLabel(state) {
  const labels = {
    waiting: 'Esperando hueco',
    starting: 'Iniciando',
    ready: 'Listo',
    recovering: 'Recuperando',
    stopped: 'Detenido'
  };
  return labels[state] || state || '—';
}
